import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { useCommon } from 'src/domains/common/Common';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { getHeaderOptions, getNewHeaderOptions } from 'src/domains/layouts/webview/components/sports/general/config';
import { MatchLeadStreamGateReasonType } from './MatchLeadState';
import {
    MatchLeadStreamGateButton,
    MatchLeadStreamGateCard,
    MatchLeadStreamGateIcon,
    MatchLeadStreamGateTitle,
    MatchLeadStreamGateWrapper,
} from './MatchLeadStreamGate.style';

interface MatchLeadStreamGatePropsType {
    event: EventModel;
    reason: MatchLeadStreamGateReasonType;
}

const getStreamGateBgImage = (sport: string): string | undefined => {
    const newHeaderBg = getNewHeaderOptions()[sport];
    if (newHeaderBg !== undefined) {
        return newHeaderBg;
    }

    return getHeaderOptions()[sport]?.desktopImage ?? undefined;
};

const renderGateTitle = (reason: MatchLeadStreamGateReasonType): JSX.Element => {
    switch (reason) {
        case 'unauthenticated':
            return (
                <I18n
                    langKey='eventStream.logInToWatch'
                    defaultText='Log in to watch live stream'
                />
            );
        case 'geoRestricted':
            return (
                <I18n
                    langKey='eventStream.notAvailableInRegion'
                    defaultText='This live stream is not available in your region due to broadcasting rights.'
                />
            );
        case 'notInPlay':
            return (
                <I18n
                    langKey='eventStream.availableWhenInPlay'
                    defaultText='Live stream will become available once the match is in play.'
                />
            );
        case 'unavailable':
            return (
                <I18n
                    langKey='eventStream.notAvailableForEvent'
                    defaultText='Live stream is not available for this event.'
                />
            );
    }
};

export const MatchLeadStreamGate = observer(
    'MatchLeadStreamGate',
    ({ event, reason }: MatchLeadStreamGatePropsType) => {
        const common = useCommon();
        const starRouter = StarRouter.get(common);
        const bgImage = getStreamGateBgImage(event.sport);

        return (
            <MatchLeadStreamGateWrapper
                bgImage={bgImage}
                data-test='match-lead-stream-gate'
                data-test-reason={reason}
            >
                <MatchLeadStreamGateCard>
                    <MatchLeadStreamGateIcon />
                    <MatchLeadStreamGateTitle>{renderGateTitle(reason)}</MatchLeadStreamGateTitle>
                    {reason === 'unauthenticated' ? (
                        <MatchLeadStreamGateButton
                            type='button'
                            onClick={(): void => starRouter.redirectToLogin()}
                            data-test='match-lead-stream-login-button'
                        >
                            <I18n
                                langKey='eventStream.logIn'
                                defaultText='Log In'
                            />
                        </MatchLeadStreamGateButton>
                    ) : null}
                </MatchLeadStreamGateCard>
            </MatchLeadStreamGateWrapper>
        );
    }
);
